import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { RoleResponseApi } from "@/api/models/role";
import { deleteRole, getRoles } from "@/api/services/roleService";
import { getAllUsers } from "@/api/services/userService";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApp } from "@/contexts/AppContext";
import { CustomizeRole } from "./CustomizeRole";
import { RolesPermissions } from "./RolesPermissions";

export function RolesPermissionsContainer() {
  const { mode } = useApp();
  const queryClient = useQueryClient();
  const [view, setView] = useState<"list" | "customize">("list");
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [isCloning, setIsCloning] = useState(false);

  const [apiRoles, setApiRoles] = useState<RoleResponseApi[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const [userRoleCounts, setUserRoleCounts] = useState<Record<string, number>>(
    {},
  );
  const [allUsers, setAllUsers] = useState<any[]>([]);

  // Force re-render when demo roles are updated
  const [demoRolesRefreshKey, setDemoRolesRefreshKey] = useState(0);

  const fetchUsers = useCallback(async () => {
    try {
      if (mode === "live") {
        const response = await getAllUsers();
        const counts: Record<string, number> = {};
        response.users.forEach((user) => {
          const roleName = user.roleName;
          counts[roleName] = (counts[roleName] || 0) + 1;
        });
        setUserRoleCounts(counts);
        setAllUsers(
          response.users.map((u) => ({
            userId: u.userId,
            name: u.name,
            email: u.email,
            roleName: u.roleName,
          })),
        );
      } else {
        // Demo mode - calculate from demo users
        const DEMO_USERS = [
          {
            userId: "1",
            name: "Catherine Hughes",
            email: "catherine.hughes@buildright.co.uk",
            forecasterRole: "CEO",
          },
          {
            userId: "2",
            name: "David Chen",
            email: "david.chen@buildright.co.uk",
            forecasterRole: "System Administrator",
          },
          {
            userId: "3",
            name: "Edna Foster",
            email: "edna.foster@buildright.co.uk",
            forecasterRole: "User",
          },
          {
            userId: "4",
            name: "Linda Kowalski",
            email: "linda.kowalski@buildright.co.uk",
            forecasterRole: "CFO",
          },
          {
            userId: "5",
            name: "Marcus Patel",
            email: "marcus.patel@buildright.co.uk",
            forecasterRole: "Regional Manager",
          },
          {
            userId: "6",
            name: "Sarah Mitchell",
            email: "sarah.mitchell@buildright.co.uk",
            forecasterRole: "Finance Manager",
          },
          {
            userId: "7",
            name: "Emma Thompson",
            email: "emma.thompson@buildright.co.uk",
            forecasterRole: "User",
          },
          {
            userId: "8",
            name: "Jennifer O'Sullivan",
            email: "jennifer.osullivan@boardmember.ie",
            forecasterRole: "External User",
          },
          {
            userId: "9",
            name: "John Smith",
            email: "john.smith@buildright.co.uk",
            forecasterRole: "User",
          },
        ];
        const counts: Record<string, number> = {};
        DEMO_USERS.forEach((user) => {
          const roleName = user.forecasterRole;
          counts[roleName] = (counts[roleName] || 0) + 1;
        });
        setUserRoleCounts(counts);
        setAllUsers(
          DEMO_USERS.map((u) => ({
            userId: u.userId,
            name: u.name,
            email: u.email,
            roleName: u.forecasterRole,
          })),
        );
      }
    } catch (err) {
      console.error("Failed to fetch users for role counts:", err);
    }
  }, [mode]);

  const fetchRoles = useCallback(async () => {
    if (mode !== "live") return;

    setRolesLoading(true);
    setRolesError(null);
    try {
      const response = await getRoles();
      setApiRoles(response.roles);
    } catch (err) {
      setRolesError(
        err instanceof Error ? err.message : "Failed to load roles",
      );
    } finally {
      setRolesLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, [fetchRoles, fetchUsers]);

  const handleCustomizeRole = (roleId: string) => {
    setSelectedRoleId(roleId);
    setView("customize");
  };

  const handleCreateCustomRole = () => {
    setSelectedRoleId(null);
    setIsCloning(false);
    setView("customize");
  };

  const handleCloneRole = (roleId: string) => {
    setSelectedRoleId(roleId);
    setIsCloning(true);
    setView("customize");
  };

  const handleBack = () => {
    setView("list");
    setSelectedRoleId(null);
    setIsCloning(false);
    if (mode === "live") {
      fetchRoles();
    } else {
      // In demo mode, increment refresh key to trigger re-render
      setDemoRolesRefreshKey((prev) => {
        console.log(
          "Incrementing demoRolesRefreshKey from",
          prev,
          "to",
          prev + 1,
        );
        return prev + 1;
      });
    }
    fetchUsers();
  };

  const handleDeleteRole = async (roleId: string) => {
    await deleteRole(Number(roleId));
    queryClient.invalidateQueries({ queryKey: ["system-roles"] });
    queryClient.invalidateQueries({ queryKey: ["roles"] });
    queryClient.invalidateQueries({ queryKey: ["users"] });
    await fetchRoles();
  };

  if (rolesLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading roles...</span>
      </div>
    );
  }

  if (rolesError && mode === "live") {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">
              Error Loading Roles
            </CardTitle>
            <CardDescription>{rolesError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={fetchRoles}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (view === "customize") {
    // Get the role name from the roleId
    let roleName = "";
    if (mode === "live" && selectedRoleId) {
      const role = apiRoles.find((r) => String(r.roleId) === selectedRoleId);
      roleName = role?.roleName || "";
    } else if (selectedRoleId) {
      // Demo mode - map roleId to role name
      const roleMap: Record<string, string> = {
        preparer: "User",
        manager: "Finance Manager",
        regional: "Regional Manager",
        cfo: "CFO",
        executive: "CEO",
        system_admin: "System Administrator",
        external: "External User",
      };
      roleName = roleMap[selectedRoleId] || "";
    }

    // Filter users for this role
    const usersInRole = allUsers
      .filter((u) => u.roleName === roleName)
      .map((u) => ({ userId: u.userId, name: u.name, email: u.email }));

    return (
      <CustomizeRole
        roleId={selectedRoleId}
        onBack={handleBack}
        mode={mode}
        isCloning={isCloning}
        usersInRole={usersInRole}
      />
    );
  }

  return (
    <RolesPermissions
      key={mode === "demo" ? demoRolesRefreshKey : "live"}
      onCustomizeRole={handleCustomizeRole}
      onCreateCustomRole={handleCreateCustomRole}
      onCloneRole={handleCloneRole}
      onDeleteRole={handleDeleteRole}
      mode={mode}
      apiRoles={apiRoles}
      userRoleCounts={userRoleCounts}
    />
  );
}
